import { useEffect, useState } from 'preact/hooks';
import { getMyEvents, getPendingPeerRatings, type MyEventsResponse, type PendingRatingEvent } from '../lib/api';
import { EventCard } from '../components/EventCard';
import { CalificarEquipo } from './CalificarEquipo';
import { Skeleton } from '../components/Skeleton';
import { TopPerformers } from '../components/TopPerformers';
import { getTelegramUser } from '../lib/telegram';
import { useEventFlows } from './EventFlows';

export function Inicio({ onOpenTienda, onGoToEventos }: { onOpenTienda?: () => void; onGoToEventos?: () => void }) {
  const [data, setData] = useState<MyEventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingRatingEvent[]>([]);
  const [calificar, setCalificar] = useState<PendingRatingEvent | null>(null);
  // Full action set (briefing, chat, reporte, cierre, swap, cancelar) shared
  // with the Eventos tab — see EventFlows.tsx. Inicio used to wire only
  // check-in/cancel here, which hid "Cerrar evento" & co. from the home card
  // and skipped the same-day RD$ 2,500 cancel warning (Codex P2 on PR #299).
  const { setSelected, busy, checkInEvent, flows } = useEventFlows({ data, setData, reload: load });

  useEffect(() => { load(); loadPending(); }, []);

  async function loadPending() {
    try {
      const r = await getPendingPeerRatings();
      setPending(r.pending || []);
    } catch { /* non-blocking — the nudge just won't show */ }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await getMyEvents();
      setData(r);
    } catch (e) {
      setError((e as Error).message || 'No pudimos cargar tus eventos');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div class="screen">
        <header class="app-header">
          <Skeleton width="50%" height={22} style={{ marginBottom: 4 }} />
          <Skeleton width="35%" height={14} />
        </header>
        <div class="card" style={{ padding: 14 }}>
          <Skeleton width="65%" height={16} style={{ marginBottom: 8 }} />
          <Skeleton width="45%" height={13} style={{ marginBottom: 6 }} />
          <Skeleton width="55%" height={13} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="error-screen">
        <h2>No pudimos cargar</h2>
        <p>{error}</p>
        <button class="btn primary" onClick={load}>Reintentar</button>
      </div>
    );
  }

  const todayEvent = data?.today?.[0];
  const pendingClosure = data?.pendingClosure || [];
  const tgUser = getTelegramUser();
  const greeting = greetingForHour();

  return (
    <div class="screen">
      <header class="app-header">
        <h1>Inicio</h1>
      </header>

      <div class="hero">
        <div class="greeting">{greeting},</div>
        <div class="name">{tgUser?.first_name || 'Hola'}</div>
        <div class="role">Plataforma Sofistics</div>
      </div>

      {todayEvent && (
        <>
          <div class="section-header">Hoy</div>
          <EventCard event={todayEvent} variant="today" onClick={() => setSelected(todayEvent)} />

          {!todayEvent.checkedIn && (
            <div style={{ margin: '0 12px' }}>
              <button
                class="btn primary"
                disabled={busy}
                onClick={() => checkInEvent(todayEvent)}
              >
                📍 Hacer check-in
              </button>
            </div>
          )}
          {todayEvent.checkedIn && (
            <div class="pill success" style={{ margin: '0 12px' }}>
              ✓ Check-in hecho
            </div>
          )}
        </>
      )}

      {!todayEvent && (
        <div class="empty">
          <div class="ic">📅</div>
          <p>No tienes eventos hoy.</p>
          <p class="hint">Revisa <strong>Eventos</strong> para ver los próximos.</p>
        </div>
      )}

      {pendingClosure.length > 0 && onGoToEventos && (
        <div class="card nav-card" onClick={onGoToEventos} style={{ borderLeft: '3px solid var(--color-danger, #ff3b30)' }}>
          <div class="nav-card-ic">🏁</div>
          <div class="nav-card-text">
            <div class="lt-title">Evento sin cerrar</div>
            <div class="lt-sub">{pendingClosure.length === 1 ? '1 evento pendiente de cierre' : `${pendingClosure.length} eventos pendientes de cierre`}</div>
          </div>
          <div class="nav-card-chev">›</div>
        </div>
      )}

      {pending.length > 0 && (
        <div class="card nav-card" onClick={() => setCalificar(pending[0])} style={{ borderLeft: '3px solid var(--brand-gold, #c79a3b)' }}>
          <div class="nav-card-ic">⭐</div>
          <div class="nav-card-text">
            <div class="lt-title">Califica a tu equipo</div>
            <div class="lt-sub">{pending.length === 1 ? '1 evento por calificar' : `${pending.length} eventos por calificar`}</div>
          </div>
          <div class="nav-card-chev">›</div>
        </div>
      )}

      {onOpenTienda && (
        <div class="card nav-card" onClick={onOpenTienda}>
          <div class="nav-card-ic">👕</div>
          <div class="nav-card-text">
            <div class="lt-title">Tienda de uniformes</div>
            <div class="lt-sub">Compra contra tu próximo pago</div>
          </div>
          <div class="nav-card-chev">›</div>
        </div>
      )}

      <TopPerformers />

      {flows}

      {calificar && (
        <CalificarEquipo
          eventId={calificar.eventId}
          eventTitle={calificar.title}
          onDone={() => { setCalificar(null); loadPending(); }}
        />
      )}
    </div>
  );
}

function greetingForHour(): string {
  // AST = UTC-4
  const ast = new Date(new Date().getTime() - 4 * 3600 * 1000);
  const h = ast.getUTCHours();
  if (h < 12) return 'Buenos días';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}
